import axios from "axios";
import { FileText, MoreVertical, Users } from "lucide-react";
import { useEffect, useState } from "react";

import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import Heading from "../../../components/AdminSideComponents/Heading";
import { capitalize, formatDate } from "../../../utils/helpers";
import ViewCustomerDetailsModal from "../../../components/AdminSideComponents/customers/ViewCustomerDetailsModal ";

const transformCustomers = (customers = []) => {
  if (!Array.isArray(customers)) return [];

  return customers.map((c) => ({
    id: c._id,
    raw: c,
    name: c?.name,
    phone: c?.phoneNumber,
    email: c?.email,
    // gender: c?.gender,
    // address: c?.address,
    customerSince: c?.createdAt,
    totalBookings: c?.totalBookings,
    // lastBookingDate: c?.lastBookingDate,
  }));
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(false);
  const [viewCustomerDetails, setViewCustomerDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE);
  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/get-all`,
      );

      const data = res?.data?.data ?? [];
      const transformed = transformCustomers(data);

      setCustomers(transformed);
      setFilteredCustomers(transformed);
    } catch (error) {
      console.error("Failed to fetch customers", error);
      setCustomers([]);
      setFilteredCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleSearch = (term) => {
    if (!term.trim()) {
      setFilteredCustomers(customers);
      return;
    }

    const lower = term.toLowerCase();
    const filtered = customers.filter(
      (c) =>
        c.name?.toLowerCase().includes(lower) ||
        c.phone?.toLowerCase().includes(lower) ||
        c.email?.toLowerCase().includes(lower),
    );

    setFilteredCustomers(filtered);
    setCurrentPage(1);
  };

  return (
    <div className="bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50">
      <div className="py-2">
        <Breadcrumb title1="Customers" title2="Customer Management" />
      </div>

      <div className="mb-6 bg-white rounded-md shadow-lg p-6">
        <Heading onSearch={handleSearch} />

        <div className="overflow-x-auto overflow-y-visible no-scrollbar">
          <table className="w-full table-fixed border border-gray-200 text-sm">
            <thead className="bg-[#EDB5A4] text-xs uppercase text-gray-700">
              <tr>
                <th className="p-2 w-12 text-center">#</th>
                <th className="p-3 w-36 text-left">Name</th>
                <th className="p-3 w-28 text-left">Phone No.</th>
                <th className="p-3 w-48 text-left">Email</th>
                {/* <th className="p-3 w-20 text-left">Gender</th> */}
                <th className="p-3 w-24 text-center">Total Bookings</th>
                {/* <th className="p-3 w-32 text-left">Last Booking</th> */}
                <th className="p-3 w-32 text-left">Member Since</th>
                {/* <th className="p-3 w-56 text-left">Address</th> */}
                <th className="p-3 w-16 text-center">Action</th>
              </tr>
            </thead>

            {isLoading ? (
              <CustomersTableSkeleton rows={ITEMS_PER_PAGE} />
            ) : filteredCustomers.length === 0 ? (
              <EmptyState />
            ) : (
              <tbody className="divide-y">
                {paginatedCustomers.map((c, index) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="p-3 py-4 text-center">
                      {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                    </td>
                    <td
                      onClick={() => {
                        setViewCustomerDetails(c.raw);
                        setIsActionDropdownOpen(null);
                      }}
                      className="p-3 cursor-pointer  font-medium truncate"
                    >
                      {c.name}
                    </td>
                    <td className="p-3">{c.phone} </td>
                    <td className="p-3 truncate max-w-[16rem]">{c.email}</td>
                    {/* <td className="p-3">{capitalize(c.gender)}</td> */}
                    <td className="p-3 text-center">{c.totalBookings}</td>
                    {/* <td className="p-3">{formatDate(c.lastBookingDate)}</td> */}
                    <td className="p-3">{formatDate(c.customerSince)}</td>
                    {/* <td className="p-3 truncate max-w-[16rem]">{c.address}</td> */}
                    <td className="relative py-4 text-center">
                      <button
                        onClick={() =>
                          setIsActionDropdownOpen((prev) =>
                            prev === c.id ? null : c.id,
                          )
                        }
                        className="p-2 rounded-lg hover:bg-gray-200 transition"
                      >
                        <MoreVertical size={20} className="text-gray-600" />
                      </button>

                      {isActionDropdownOpen === c.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setIsActionDropdownOpen(null)}
                          />
                          <div
                            className={`absolute w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 right-12 top-3`}
                          >
                            <button
                              onClick={() => {
                                setViewCustomerDetails(c.raw);
                                setIsActionDropdownOpen(null);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-100 flex items-center gap-3"
                            >
                              <FileText size={16} />
                              View Details
                            </button>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <p className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </p>

            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                Prev
              </button>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        <ViewCustomerDetailsModal
          customer={viewCustomerDetails}
          onClose={() => setViewCustomerDetails(null)}
        />
      </div>
    </div>
  );
};

const CustomersTableSkeleton = ({ rows = 10 }) => {
  return (
    <tbody>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          {Array.from({ length: 7 }).map((_, j) => (
            <td key={j} className="p-3">
              <div className="h-8 bg-gray-200 rounded" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
};

const EmptyState = () => (
  <tbody>
    <tr>
      <td colSpan={6} className="py-16 text-center">
        <div className="flex flex-col items-center justify-center gap-4 text-gray-500">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
            <Users className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700">
            No customers found
          </h3>
          <p className="text-sm">
            Customers will appear here once bookings are made.
          </p>
        </div>
      </td>
    </tr>
  </tbody>
);

export default Customers;
